import React, { useState } from 'react';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { ShieldCheck, Globe, Menu, X, Zap } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const navLinkClass = ({
    isActive
  }: {
    isActive: boolean;
  }) => `transition-colors relative py-2 ${isActive ? 'text-federal-blue font-black' : 'text-tech-slate hover:text-federal-blue'}`;
  const mobileNavLinkClass = ({
    isActive
  }: {
    isActive: boolean;
  }) => `block py-4 text-lg font-bold border-b border-slate-100 ${isActive ? 'text-federal-blue' : 'text-tech-slate'}`;
  const activeIndicator = ({
    isActive
  }: {
    isActive: boolean;
  }) => isActive ? <motion.span layoutId="nav-underline" className="absolute -bottom-1 left-0 w-full h-0.5 bg-muted-gold" transition={{
    type: 'spring',
    stiffness: 380,
    damping: 32
  }} data-id="element-235" /> : null;
  const navItems = [{
    to: '/comply-plus',
    label: 'Comply+'
  }, {
    to: '/capabilities',
    label: 'Capabilities'
  }, {
    to: '/legacy',
    label: 'Our Legacy'
  }, {
    to: '/locations',
    label: 'Locations'
  }, {
    to: '/entity-data',
    label: 'Entity Data'
  }, {
    to: '/contact',
    label: 'Contact Us'
  }];
  return <motion.header initial={{
    y: -40,
    opacity: 0
  }} animate={{
    y: 0,
    opacity: 1
  }} transition={{
    duration: 0.5,
    ease: [0.22, 1, 0.36, 1]
  }} className="sticky top-0 z-50 w-full" data-id="element-236">
      {/* Top Utility Bar */}
      <div className="bg-federal-blue text-white py-2 px-4 md:px-8 border-b border-white/10" data-id="element-237">
        <div className="max-w-7xl mx-auto flex flex-wrap justify-between items-center text-[10px] md:text-xs font-medium tracking-wider uppercase" data-id="element-238">
          <div className="flex gap-4 md:gap-8" data-id="element-239">
            <span className="flex items-center gap-1.5 opacity-90" data-id="element-240">
              <ShieldCheck className="w-3 h-3 text-muted-gold" data-id="element-241" />
              Socio-economic Status: WOSB / EDWOSB
            </span>
            <span className="flex items-center gap-1.5 opacity-90" data-id="element-242">
              <Globe className="w-3 h-3 text-muted-gold" data-id="element-243" />
              Registered: SAM.gov / UEI Ready
            </span>
          </div>
          <div className="hidden md:block opacity-70" data-id="element-244">
            CAGE: 9XYL4 | UEI: ZK82N9L1M4P7
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="bg-white border-b border-slate-200 shadow-sm relative z-50" data-id="element-245">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-20 flex items-center justify-between" data-id="element-246">
          <Link to="/" className="flex items-center gap-2" onClick={() => setIsMenuOpen(false)} data-id="element-247">
            <div className="w-12 h-10 flex items-center justify-center overflow-hidden" data-id="element-248">
              <img src="https://cdn.magicpatterns.com/uploads/cMrkjmuEGCTQik26MvLHNF/ChatGPT_Image_Apr_14,_2026,_01_45_07_PM.png" alt="Codes & Cogs Logo" className="h-full w-auto object-contain" data-id="element-249" />
            </div>
            <div className="flex flex-col leading-tight" data-id="element-250">
              <span className="text-federal-blue font-black text-xl tracking-tighter uppercase" data-id="element-251">
                Codes & Cogs
              </span>
              <span className="text-tech-slate text-[10px] font-bold tracking-[0.2em] uppercase" data-id="element-252">
                Federal Solutions
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8 text-sm font-semibold text-tech-slate" data-id="element-253">
            {navItems.map(item => <NavLink key={item.to} to={item.to} className={navLinkClass} data-id="element-254">
                {({
              isActive
            }) => <>
                    {item.label}
                    {activeIndicator({
                isActive
              })}
                  </>}
              </NavLink>)}
          </div>

          <div className="flex items-center gap-4" data-id="element-255">
            <motion.button whileHover={{
            scale: 1.03
          }} whileTap={{
            scale: 0.97
          }} onClick={() => navigate('/comply-plus')} className="hidden sm:flex bg-federal-blue hover:bg-federal-blue/90 text-white px-6 py-2.5 rounded-sm font-bold text-sm items-center gap-2 transition-all shadow-md" data-id="element-256">
              <Zap className="w-4 h-4 text-muted-gold" data-id="element-257" />
              <span data-id="element-258">Comply+ Platform</span>
            </motion.button>

            {/* Mobile Menu Toggle */}
            <button className="lg:hidden p-2 text-federal-blue hover:bg-slate-50 rounded-md transition-colors" onClick={() => setIsMenuOpen(!isMenuOpen)} aria-label="Toggle menu" data-id="element-259">
              {isMenuOpen ? <X className="w-6 h-6" data-id="element-260" /> : <Menu className="w-6 h-6" data-id="element-261" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        <AnimatePresence data-id="element-262">
          {isMenuOpen && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.3,
          ease: 'easeInOut'
        }} className="lg:hidden bg-white border-t border-slate-100 overflow-hidden shadow-xl" data-id="element-263">
              <div className="px-4 py-6 space-y-2" data-id="element-264">
                {navItems.map(item => <NavLink key={item.to} to={item.to} className={mobileNavLinkClass} onClick={() => setIsMenuOpen(false)} data-id="element-265">
                    {item.label}
                  </NavLink>)}

                <div className="pt-6" data-id="element-266">
                  <button onClick={() => {
                navigate('/comply-plus');
                setIsMenuOpen(false);
              }} className="w-full bg-federal-blue text-white py-4 rounded-sm font-bold flex items-center justify-center gap-2" data-id="element-267">
                    <Zap className="w-4 h-4 text-muted-gold" data-id="element-268" />
                    Comply+ Platform
                  </button>
                </div>

                <div className="pt-6 border-t border-slate-100" data-id="element-269">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4" data-id="element-270">
                    Entity Identifiers
                  </p>
                  <div className="grid grid-cols-2 gap-4 text-[10px] font-bold text-federal-blue" data-id="element-271">
                    <div data-id="element-272">
                      <span className="text-slate-400 block mb-1" data-id="element-273">CAGE</span>
                      9XYL4
                    </div>
                    <div data-id="element-274">
                      <span className="text-slate-400 block mb-1" data-id="element-275">UEI</span>
                      ZK82N9L1M4P7
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </nav>
    </motion.header>;
}